<template>
  <modal
    :name="modalName"
    :adaptive="true"
    :scrollable="true"
    width="90%"
    :max-width="680"
    height="auto"
    @opened="onOpened"
    @closed="onClosed"
  >
    <div class="v--modal-close-top">
      <el-button @click="close" icon="el-icon-close" type="text"></el-button>
    </div>

    <div class="summarize-modal-content">
      <div class="summarize-title">文章を短くする</div>
      <p class="summarize-lead">
        入力欄をオーバーしています。目標
        <strong>{{ targetLength }}</strong> 字程度まで短縮した提案を
        <strong>{{ variationCount }}</strong> パターンご用意します。
      </p>

      <!-- 取得中 -->
      <div v-if="loading" class="summarize-loading">
        <i class="el-icon-loading"></i>
        <span>AI が短縮案を作成しています…</span>
      </div>

      <!-- エラー -->
      <div v-else-if="errorMessage" class="summarize-error">
        <i class="el-icon-warning"></i> {{ errorMessage }}
        <div class="summarize-error-actions">
          <el-button size="small" @click="fetchSummaries"
            >もう一度試す</el-button
          >
        </div>
      </div>

      <!-- 候補表示 -->
      <div v-else-if="summaries.length > 0" class="summarize-body">
        <div class="summarize-tabs">
          <button
            v-for="(item, index) in summaries"
            :key="index"
            class="summarize-tab"
            :class="{ 'is-active': activeIndex === index }"
            @click="activeIndex = index"
          >
            案{{ index + 1 }}
            <span class="summarize-tab-length">{{ item.length }}字</span>
          </button>
        </div>

        <div class="summarize-preview">
          <div class="summarize-meta">
            <span
              >短縮案 {{ activeIndex + 1 }}：{{ currentSummary.length }}字</span
            >
            <span class="summarize-meta-diff">
              （元: {{ originalLength }}字 / 差分 {{ diffText }}）
            </span>
          </div>
          <div class="summarize-text">{{ currentSummary.text }}</div>
        </div>

        <div class="summarize-actions">
          <el-button @click="close">キャンセル</el-button>
          <el-button type="primary" @click="applyCurrent"
            >この案を採用</el-button
          >
        </div>
      </div>

      <!-- 候補ゼロ（LLM が短縮不要と判断、または API が空返し） -->
      <div v-else class="summarize-empty">
        <i class="el-icon-info"></i>
        短縮案を生成できませんでした。文章を少し編集してから再度お試しください。
        <div class="summarize-error-actions">
          <el-button @click="close">閉じる</el-button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { buildSummarizeFetcher } from './summarize-api'

export default {
  name: 'SummarizeModal',
  props: {
    modalName: {
      type: String,
      default: 'summarize-modal'
    },
    text: {
      type: String,
      default: ''
    },
    targetLength: {
      type: Number,
      required: true
    },
    variationCount: {
      type: Number,
      default: 3
    },
    context: {
      type: String,
      default: '文章'
    }
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      summaries: [],
      activeIndex: 0,
      abortController: null,
      applied: false
    }
  },
  computed: {
    originalLength() {
      return (this.text || '').length
    },
    currentSummary() {
      return this.summaries[this.activeIndex] || { text: '', length: 0 }
    },
    diffText() {
      const d = this.currentSummary.length - this.originalLength
      if (d === 0) return '±0字'
      return (d > 0 ? '+' : '') + d + '字'
    }
  },
  methods: {
    close() {
      this.cancelInflight()
      this.$modal.hide(this.modalName)
    },
    onOpened() {
      this.activeIndex = 0
      this.errorMessage = ''
      this.summaries = []
      this.applied = false
      this.fetchSummaries()
    },
    onClosed() {
      this.cancelInflight()
      // 候補が提示されたのに採用せず閉じた = 明示破棄として記録
      if (!this.applied && this.summaries.length > 0) {
        this.reportEvent('dismiss')
      }
    },
    cancelInflight() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
      this.loading = false
    },
    fetchSummaries() {
      this.cancelInflight()
      this.errorMessage = ''
      this.summaries = []

      if (!this.text || this.text.length < 50) {
        this.errorMessage = '文章が短いため、短縮提案の対象外です。'
        return
      }
      if (this.targetLength <= 0 || this.targetLength >= this.text.length) {
        this.errorMessage = '目標字数が不正です。'
        return
      }

      const fetcher = buildSummarizeFetcher(this)
      if (!fetcher) {
        this.errorMessage =
          '短縮機能が利用できません。時間をおいて再度お試しください。'
        return
      }

      const controller =
        typeof AbortController !== 'undefined' ? new AbortController() : null
      this.abortController = controller
      this.loading = true

      fetcher({
        text: this.text,
        targetLength: this.targetLength,
        variationCount: this.variationCount,
        context: this.context,
        signal: controller ? controller.signal : null
      })
        .then((summaries) => {
          if (this.abortController !== controller) return
          this.abortController = null
          this.loading = false
          this.summaries = Array.isArray(summaries) ? summaries : []
          this.activeIndex = 0
          if (this.summaries.length === 0) {
            // 空配列は UI 側で専用メッセージ表示
          }
        })
        .catch((err) => {
          if (this.abortController !== controller) return
          this.abortController = null
          this.loading = false
          if (
            this.$axios &&
            this.$axios.isCancel &&
            this.$axios.isCancel(err)
          ) {
            return
          }
          const status = err && err.response && err.response.status
          if (status === 401 || err.code === 'UNAUTHENTICATED') {
            this.errorMessage = 'ログインが必要です。'
          } else if (status === 403) {
            this.errorMessage = 'ヤギプライム会員限定機能です。'
          } else if (status === 429) {
            this.errorMessage =
              '本日の利用回数上限に達しました。明日またお試しください。'
          } else if (status === 502) {
            this.errorMessage =
              'AIサービスが一時的に利用できません。しばらくしてから再度お試しください。'
          } else {
            this.errorMessage =
              '短縮案の取得に失敗しました。しばらくしてから再度お試しください。'
          }
        })
    },
    applyCurrent() {
      const picked = this.currentSummary
      if (!picked || !picked.text) return
      this.applied = true
      this.reportEvent('accept', { candidateIndex: this.activeIndex })
      this.$emit('apply', picked.text)
      this.$modal.hide(this.modalName)
    },
    // 採用 / 破棄イベントを varuna に記録（fire-and-forget・既存 fetcher と同じ uid 取得）
    reportEvent(action, extra) {
      const uid =
        (this.$firebase && this.$firebase.user && this.$firebase.user.uid) ||
        null
      const endpoint =
        this.$varuna &&
        this.$varuna.endPoints &&
        this.$varuna.endPoints.proofreadEvent
      if (!uid || !endpoint) return
      const payload = { uuid: uid, feature: 'summarize', action }
      if (extra && typeof extra.candidateIndex === 'number') {
        payload.candidate_index = extra.candidateIndex
      }
      try {
        this.$axios.post(endpoint, payload, { timeout: 5000 }).catch(() => {})
      } catch (e) {
        /* noop */
      }
    }
  }
}
</script>

<style scoped>
.summarize-modal-content {
  padding: 24px 20px 20px;
}
.summarize-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
}
.summarize-lead {
  font-size: 13px;
  color: #666;
  margin: 0 0 16px;
}
.summarize-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #888;
  padding: 24px 0;
  justify-content: center;
}
.summarize-loading i {
  font-size: 20px;
}
.summarize-error {
  color: #c0392b;
  background: #fdecea;
  padding: 12px 14px;
  border-radius: 6px;
  font-size: 14px;
}
.summarize-error-actions {
  margin-top: 10px;
  text-align: right;
}
.summarize-empty {
  padding: 18px 0;
  color: #666;
  font-size: 14px;
  text-align: center;
}
.summarize-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.summarize-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.summarize-tab {
  flex: 1 1 0;
  min-width: 90px;
  padding: 10px 8px;
  border: 1px solid #d0d0d0;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #444;
  transition: all 0.15s;
}
.summarize-tab:hover {
  border-color: #3b82f6;
}
.summarize-tab.is-active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}
.summarize-tab-length {
  display: block;
  font-size: 11px;
  margin-top: 2px;
  opacity: 0.85;
}
.summarize-preview {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px 14px;
  background: #fafafa;
}
.summarize-meta {
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.summarize-meta-diff {
  color: #3b82f6;
}
.summarize-text {
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.7;
  color: #222;
  max-height: 260px;
  overflow-y: auto;
}
.summarize-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}
</style>
